import axios, { AxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserRole, UserInviteStatus } from '../../models/user';
import { toast } from 'react-toastify';
import StandardPage from '../../components/StandardPage';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/organization';
import { useMembers } from '../../context/members';
import { Dialog, Transition } from '@headlessui/react';
import {
  trackMembersInviteOpened,
  trackMembersInviteSent,
  trackMembersRemoved,
  trackMembersRoleUpdated,
} from '../../analytics';

type SortKey = 'email' | 'date_joined' | 'role' | 'status';
type SortDir = 'asc' | 'desc';

const roleLabel = (r: UserRole) => r.charAt(0).toUpperCase() + r.slice(1);
const badge = (txt: string, variant: 'gray'|'amber'|'blue'|'red'|'green') =>
  `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
    variant === 'amber' ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'
    : variant === 'blue' ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-200'
    : variant === 'red' ? 'bg-rose-100 text-rose-800 ring-1 ring-rose-200'
    : variant === 'green' ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200'
    : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
  }`;

const emailOK = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const useDebounced = (val: string, ms = 300) => {
  const [v, setV] = useState(val);
  useEffect(() => { const t = setTimeout(() => setV(val), ms); return () => clearTimeout(t); }, [val, ms]);
  return v;
};

const MembersPage: React.FC = () => {
  const { members, setMembers, isLoading } = useMembers();
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [newMemberEmail, setNewMemberEmail] = useState<string>('');
  const [newMemberRole, setNewMemberRole] = useState<UserRole>(UserRole.MEMBER);
  const [inviteLoading, setInviteLoading] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const { getAccessTokenSilently } = useAuth0();
  const { organization } = useOrganization();

  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [statusFilter, setStatusFilter] = useState<'ALL'|'PENDING'|'ACCEPTED'>('ALL');
  const [sortKey, setSortKey] = useState<SortKey>('date_joined');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const openInviteModal = useCallback((source: string) => {
    trackMembersInviteOpened({ source });
    setShowInviteModal(true);
  }, []);

  // shortcuts
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if ((e.key === 'i' || e.key === 'I') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        openInviteModal('shortcut');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openInviteModal]);

  const loggedInUser = organization?.me;
  const isLoggedInUserAdmin = organization?.me.role === UserRole.ADMIN;
  const adminCount = useMemo(() => members.filter(m => m.role === UserRole.ADMIN).length, [members]);

  const canChangeRole = (email: string) =>
    isLoggedInUserAdmin && loggedInUser?.email !== email;

  const canRemove = (email: string, role: UserRole) => {
    if (!isLoggedInUserAdmin) return false;
    if (loggedInUser?.email === email) return false;
    if (role === UserRole.ADMIN && adminCount <= 1) return false;
    return true;
  };

  const inviteMember = async () => {
    const email = newMemberEmail.trim();
    if (!email) return toast.error('Please enter an email address');
    if (!emailOK(email)) return toast.error('Please enter a valid email address');

    try {
      const token = await getAccessTokenSilently();
      setInviteLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/organization/members`,
        { email, role: newMemberRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(prev => [
        ...prev,
        { email, role: newMemberRole, status: UserInviteStatus.PENDING }
      ]);
      trackMembersInviteSent({ role: newMemberRole });
      toast.success('Member invited successfully');
      setShowInviteModal(false);
      setNewMemberEmail('');
    } catch (err) {
      const msg = (err as AxiosError)?.response?.data as any;
      toast.error((msg?.message as string) || (err as AxiosError).message);
    } finally {
      setInviteLoading(false);
    }
  };

  const updateMemberRole = async (memberEmail: string, newRole: UserRole) => {
    if (!canChangeRole(memberEmail)) return;
    if (memberEmail === loggedInUser?.email) return;

    if (newRole !== UserRole.ADMIN) {
      const target = members.find(m => m.email === memberEmail);
      if (target?.role === UserRole.ADMIN && adminCount <= 1) {
        toast.error('You must keep at least one admin.');
        return;
      }
    }

    try {
      const token = await getAccessTokenSilently();
      setRoleLoading(memberEmail);
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/organization/members`,
        { email: memberEmail, new_role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(prev => prev.map(m => m.email === memberEmail ? { ...m, role: newRole } : m));
      trackMembersRoleUpdated({ role: newRole });
      toast.success('Member role updated');
    } catch (err) {
      const msg = (err as AxiosError)?.response?.data as any;
      toast.error((msg?.message as string) || (err as AxiosError).message);
    } finally {
      setRoleLoading(null);
    }
  };

  const confirmRemoveMember = (email: string) => {
    setMemberToRemove(email);
    setShowConfirmModal(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    const target = members.find(m => m.email === memberToRemove);
    if (!target) return setShowConfirmModal(false);

    if (!canRemove(target.email, target.role)) {
      toast.error('You cannot remove this member.');
      return;
    }

    try {
      setRemoveLoading(true);
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/organization/members`,
        { data: { email: memberToRemove }, headers: { Authorization: `Bearer ${token}` } } // ✅ fix
      );
      setMembers(prev => prev.filter(m => m.email !== memberToRemove));
      trackMembersRemoved({
        role: target.role,
        status: target.status === UserInviteStatus.PENDING ? 'pending' : 'accepted',
      });
      toast.success('Member removed');
    } catch (err) {
      const msg = (err as AxiosError)?.response?.data as any;
      toast.error((msg?.message as string) || (err as AxiosError).message);
    } finally {
      setShowConfirmModal(false);
      setMemberToRemove(null);
      setRemoveLoading(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const txt = dq.toLowerCase();
    const base = members.filter(m => {
      const status = m.status === UserInviteStatus.PENDING ? 'PENDING' : 'ACCEPTED';
      const statusOk = statusFilter === 'ALL' ? true : status === statusFilter;
      const qOk = txt
        ? m.email.toLowerCase().includes(txt) || roleLabel(m.role).toLowerCase().includes(txt)
        : true;
      return statusOk && qOk;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = base.sort((a, b) => {
      if (sortKey === 'email') return a.email.localeCompare(b.email) * dir;
      if (sortKey === 'role') return a.role.localeCompare(b.role) * dir;
      if (sortKey === 'status') {
        const sa = a.status === UserInviteStatus.PENDING ? 0 : 1;
        const sb = b.status === UserInviteStatus.PENDING ? 0 : 1;
        return (sa - sb) * dir;
      }
      const ta = a.date_joined ? new Date(a.date_joined).getTime() : 0;
      const tb = b.date_joined ? new Date(b.date_joined).getTime() : 0;
      return (ta - tb) * dir;
    });
    return sorted;
  }, [members, dq, statusFilter, sortKey, sortDir]);

  const header = (label: string, key?: SortKey) => {
    const active = key && sortKey === key;
    const next = active && sortDir === 'asc' ? 'desc' : 'asc';
    return (
      <th
        className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 cursor-pointer select-none"
        onClick={key ? () => { setSortKey(key); setSortDir(next as SortDir); } : undefined}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {key && (
            <svg className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-30'}`} viewBox="0 0 24 24" fill="currentColor">
              {active && sortDir === 'asc' ? <path d="M7 14l5-5 5 5z"/> : <path d="M7 10l5 5 5-5z"/>}
            </svg>
          )}
        </span>
      </th>
    );
  };

  const domainHint = useMemo(() => {
    const orgEmail = loggedInUser?.email || '';
    const dom = orgEmail.split('@')[1];
    return dom ? `Tip: invite teammates like name@${dom}` : '';
  }, [loggedInUser]);

  return (
    <StandardPage title="Members">
      <div className="w-full rounded-xl bg-white p-6 shadow">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="hidden text-xs text-slate-500 sm:block">Press <kbd className="rounded border px-1">/</kbd> to search, <kbd className="rounded border px-1">I</kbd> to invite</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
            </select>
            <input
              ref={searchRef}
              type="search"
              placeholder="Search members…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-64 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
            />
            {isLoggedInUserAdmin && (
              <button
                onClick={() => openInviteModal('button')}
                className="rounded-md bg-[#3e82f8] px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
              >
                + Invite
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          ) : (
            <table className="min-w-[720px] w-full">
              <thead className="bg-slate-50">
                <tr className="border-b">
                  {header('Email', 'email')}
                  {header('Date Joined', 'date_joined')}
                  {header('Role', 'role')}
                  {header('Status', 'status')}
                  <th className="py-3 px-3" />
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map(member => {
                  const status = member.status === UserInviteStatus.PENDING ? 'PENDING' : 'ACCEPTED';
                  return (
                    <tr key={member.email} className="border-b text-slate-900 hover:bg-slate-50">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
                            {member.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{member.email}</div>
                            {status === 'PENDING' && <span className={badge('Pending', 'amber')} />}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        {member.date_joined ? new Date(member.date_joined).toLocaleDateString() : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="py-3 px-3">
                        {canChangeRole(member.email) ? (
                          <select
                            value={member.role}
                            onChange={e => updateMemberRole(member.email, e.target.value as UserRole)}
                            disabled={roleLoading === member.email}
                            className={`rounded-md border border-slate-300 px-2 py-1 text-sm ${roleLoading === member.email ? 'bg-slate-100' : ''}`}
                          >
                            <option value={UserRole.ADMIN}>Admin</option>
                            <option value={UserRole.MEMBER}>Member</option>
                          </select>
                        ) : (
                          <span className={badge(roleLabel(member.role), member.role === UserRole.ADMIN ? 'blue' : 'gray')} />
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {status === 'ACCEPTED'
                          ? <span className={badge('Active', 'green')} />
                          : <span className={badge('Invited', 'amber')} />}
                      </td>
                      <td className="py-3 px-3">
                        {canRemove(member.email, member.role) && (
                          <button
                            onClick={() => confirmRemoveMember(member.email)}
                            className="rounded-md p-2 text-rose-600 hover:bg-rose-50"
                            title="Remove member"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredSorted.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-500">No members match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <Transition show={showInviteModal} as={React.Fragment}>
        <Dialog onClose={() => !inviteLoading && setShowInviteModal(false)} className="relative z-[9999]">
          <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="mb-1 text-lg font-semibold text-slate-900">Invite Member</Dialog.Title>
                  <p className="mb-4 text-xs text-slate-500">{domainHint}</p>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-slate-700">Email</label>
                    <input
                      type="email"
                      placeholder="name@example.com"
                      value={newMemberEmail}
                      onChange={e => setNewMemberEmail(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700">Role</label>
                    <select
                      value={newMemberRole}
                      onChange={e => setNewMemberRole(e.target.value as UserRole)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                    >
                      <option value={UserRole.ADMIN}>Admin</option>
                      <option value={UserRole.MEMBER}>Member</option>
                    </select>
                  </div>
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      onClick={() => setShowInviteModal(false)}
                      disabled={inviteLoading}
                      className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={inviteMember}
                      disabled={inviteLoading || !emailOK(newMemberEmail)}
                      className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {inviteLoading ? 'Sending…' : 'Send Invite'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Remove Confirm Modal */}
      <Transition show={showConfirmModal} as={React.Fragment}>
        <Dialog onClose={() => !removeLoading && setShowConfirmModal(false)} className="relative z-[9999]">
          <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">Confirm Removal</Dialog.Title>
                  <p className="mt-2 text-sm text-slate-600">Are you sure you want to remove this member? This action cannot be undone.</p>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setShowConfirmModal(false)}
                      disabled={removeLoading}
                      className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRemoveMember}
                      disabled={removeLoading}
                      className="rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      {removeLoading ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </StandardPage>
  );
};

export default MembersPage;
